import React, { useEffect, useState, MouseEvent, useCallback, ReactElement, useRef } from "react";
import { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import { TabsProps, TabList, TabPanel, Tab } from "react-tabs";
import NFTCardsWindow from "@/containers/nft/NFTCardsWindow";
import NFTCard from "@/components/nft/NFTCard";
import * as model from "@/interfaces/nftTypes";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import { NFTCardItem, NFTItemClickFunc, ClickType } from "@/interfaces/nftTypes";
import { useFilterCovers } from "@/hooks/useRequest/useNFTRequest";
import { getCoverCategories, likeCover, unlikeCover } from "@/service/nft.services";
import useWindowSize from "@/hooks/useWindowSize";
import birdge from "@/utils/dsBridge";
import { BridgeCallHandlers } from "@/interfaces/enums";
import { debounce } from "lodash";
import Head from "next/head";
import { convertCoverName2Abbr } from "@/utils/util";
import RFLayout from "@/layout/RFLayout";

import { produce } from "immer";

import NFTShare from "@/containers/nft/NFTShare";
import EmptyContainerV2 from "@/containers/common/EmptyContainerv2";
import CustomerModal from "@/containers/common/CustomerModal";
import Button from "@/components/Button";
import { ShareIcon } from "@/components/RFIcon";
const Tabs = dynamic<TabsProps>(
	import("react-tabs").then((mod) => mod.Tabs),
	{ ssr: false },
);

export default function NFTCovers({ categories }: { categories: model.CategoryTab[] }) {
	const { t } = useTranslation("nft");
	const { width, height } = useWindowSize();
	const [modalSize, setModalSize] = useState({ height: 447, width: 300 });
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [tabIndex, setTabIndex] = useState(0);
	const [selectTab, setSelectTab] = useState({ type: "tag", value: "like" });
	const [mobileClientCoverId, setMobileClientCoverId] = useState("");
	const [activeCard, setActiveCard] = useState<NFTCardItem>({
		loadUrl: "",
		name: "",
		likedBy: 0,
		owner: "",
	});

	const { assets, mutate, isLoading } = useFilterCovers(selectTab);
	// 分享
	const shareRef = useRef<any>(null);

	useEffect(() => {
		const data = birdge?.call(BridgeCallHandlers.GET_SELECTD_NFT_COVER);
		if (data) {
			const clientCover = JSON.parse(data);
			setMobileClientCoverId(clientCover.coverId);
		}
	}, []);

	useEffect(() => {
		// 左右边距 38, 高/宽 = 1.4
		const modalWidth = width - 38 * 2;
		const modalStyle = {
			width: modalWidth,
			height: modalWidth * 1.4,
		};
		setModalSize(modalStyle);
	}, [height, width]);

	const handleLikeChange = async (coverId: string, liked: boolean, tabValue: string) => {
		mutate((data) => {
			const newData = produce(data, (draft) => {
				draft?.map((item) => {
					if (item.coverId === coverId) {
						liked ? item.desireCounts++ : item.desireCounts--;
						item.desiredMyself = liked;
					}
				});
			});
			return newData ? [...newData] : undefined;
		}, false);

		let isOk = false;
		if (liked) {
			isOk = await likeCover(coverId);
		} else {
			isOk = await unlikeCover(coverId);
		}
		// 乐观更新， 仅like页面和失败时， 需要再次刷新列表
		if (!isOk || tabValue == "like") {
			mutate();
		}
	};

	const debouncedLikeClickHandler = useCallback(
		debounce((coverId: string, liked: boolean, tabValue: string) => handleLikeChange(coverId, liked, tabValue), 300),
		[],
	);
	const handleCardClick: NFTItemClickFunc = (itemId, type, liked) => {
		const item = assets?.find((item) => item.coverId === itemId);
		if (!item) {
			return;
		}

		if (type === ClickType.NFT_CARD_LIKED_ICON) {
			debouncedLikeClickHandler(item.coverId, liked as boolean, selectTab.value);
			return;
		}
		setActiveCard({
			loadUrl: item.coverUrl,
			name: item.name,
			likedBy: item.desireCounts,
			owner: "--",
			id: item.coverId,
		});
		setVisible(true);
	};

	const handleSelected = (e: MouseEvent) => {
		e.stopPropagation();
		birdge?.call(BridgeCallHandlers.ON_NFT_COVER_SELECT, {
			coverId: activeCard.id,
			coverUrl: activeCard.loadUrl,
		});
		setLoading(true);
		setTimeout(() => {
			setLoading(false);
		}, 500);
	};

	const renderShare = () => {
		return (
			<div
				className="absolute right-4 top-3 bg-dark/[7%] rounded-full flex items-center justify-center w-9 h-9"
				onClick={() => {
					if (!shareRef.current) return;
					shareRef.current
						.handleShare()
						.then(() => {
							setVisible(false);
						})
						.catch(() => {});
				}}
			>
				<ShareIcon className="w-5 h-5 text-white" />
			</div>
		);
	};

	const renderModalBtnText = () => {
		return isSelectedByMobileClient() ? t("selected") : t("select-as-cover");
	};

	const renderTabPanelContent = (index: number) => {
		let list: Array<NFTCardItem> = Array(12).fill({
			loadUrl: "",
		});

		if (!assets) {
			return <NFTCardsWindow list={list} onItemClick={handleCardClick} />;
		}

		if (assets.length === 0) {
			return <EmptyContainerV2 title={t("nothing-here", { ns: "common" })} subTitle={t("like-tab-empty-cover")} />;
		}

		list = assets.map((asset) => ({
			id: asset.coverId,
			loadUrl: asset.coverUrl,
			name: convertCoverName2Abbr(asset.name),
			likedBy: asset.desireCounts,
			isFavorite: asset.desiredMyself,
			withLikedIcon: true,
		}));
		return <NFTCardsWindow list={list} onItemClick={handleCardClick} />;
	};

	const handleTabClick = (index: number) => {
		const tabItem = categories[index];
		setSelectTab({
			type: tabItem.type,
			value: encodeURIComponent(tabItem.value),
		});
		setTabIndex(index);
	};

	const isSelectedByMobileClient = () => {
		return activeCard.id === mobileClientCoverId;
	};

	return (
		<>
			<Head>
				<title>{t("rockflow-digital-cover")}</title>
			</Head>
			<div className="h-full">
				<Tabs selectedIndex={tabIndex} onSelect={handleTabClick}>
					<TabList className="nft_tab__list hidden-scroll-bar">
						{categories.map((category) => (
							<Tab className="nft_tab__item" selectedClassName="nft_tab__item__active" key={category.value}>
								{t(`${category.type}-${category.value}`.toLocaleLowerCase())}
							</Tab>
						))}
					</TabList>
					{categories.map((category, index) => (
						<TabPanel key={category.value}>{renderTabPanelContent(index)}</TabPanel>
					))}
				</Tabs>

				<NFTShare ref={shareRef} type={2} imgUrl={activeCard.loadUrl} />

				{/* 选择弹窗 */}
				<CustomerModal visible={visible} onClose={() => setVisible(false)} bgLevel="deep">
					<div
						className="w-[90vw] max-w-sm"
						onClick={(e) => {
							if (e.target === e.currentTarget) {
								setVisible(false);
							}
						}}
					>
						<div style={{ ...modalSize }} className="mx-auto relative">
							<NFTCard
								loadUrl={activeCard.loadUrl}
								name={activeCard.name}
								owner={activeCard.owner}
								likedBy={activeCard.likedBy}
								loadLarge
							/>
							{renderShare()}
							<div className="mx-auto mt-6" style={{ width: modalSize.width + "px" }}>
								<Button
									block
									type="primary"
									loading={loading}
									onClick={(e) => handleSelected(e)}
									disabled={isSelectedByMobileClient()}
								>
									{renderModalBtnText()}
								</Button>
							</div>
						</div>
					</div>
				</CustomerModal>
			</div>
		</>
	);
}
export const getServerSideProps: GetServerSideProps = async ({ res, locale = "en" }) => {
	res.setHeader("Cache-Control", "public, s-maxage=100, stale-while-revalidate=99");
	const data = await getCoverCategories();
	return {
		props: {
			categories: data,
			...(await serverSideTranslations(locale, ["error", "common", "nft", "share"])),
		},
	};
};

NFTCovers.getLayout = function getLayout(page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};
